/**
 * UserProfileContext — provides PAPI employee profile + derived SenderInfo.
 *
 * Fetches the current user's profile from Gateway PAPI proxy on login,
 * caches it, and exposes a `senderInfo` object ready for chat requests.
 *
 * Usage:
 *   const { senderInfo, profile, isLoading } = useUserProfile();
 */

import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
  gatewayClient,
  GatewayClient,
  PapiEmployeeResponse,
  SenderInfo,
} from '../client/GatewayClient';

// ── Types ───────────────────────────────────────────────────────────────────

interface UserProfileContextValue {
  /** Full PAPI employee response (null until loaded) */
  profile: PapiEmployeeResponse | null;
  /** Derived sender info for chat requests (null until loaded) */
  senderInfo: SenderInfo | null;
  /** Whether the profile is still loading */
  isLoading: boolean;
  /** Error message if profile fetch failed */
  error: string | null;
  /** Force re-fetch the profile */
  refresh: () => void;
}

const UserProfileContext = createContext<UserProfileContextValue>({
  profile: null,
  senderInfo: null,
  isLoading: true,
  error: null,
  refresh: () => {},
});

export const useUserProfile = () => useContext(UserProfileContext);

// ── Provider ────────────────────────────────────────────────────────────────

export function UserProfileProvider({ children }: { children: React.ReactNode }) {
  const { uid, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<PapiEmployeeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !uid) {
      setProfile(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setProfile(null);
    setIsLoading(true);
    setError(null);

    gatewayClient
      .getEmployee(uid, 'job,work-location', { skipCache: refreshKey > 0 })
      .then((data) => {
        if (!cancelled) {
          setProfile(data);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[UserProfile] Failed to fetch PAPI profile:', err);
          setError(err.message || 'Failed to load user profile');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [uid, isAuthenticated, refreshKey]);

  const senderInfo = useMemo(() => {
    if (!profile) return null;
    return GatewayClient.toSenderInfo(profile);
  }, [profile]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const value = useMemo(
    () => ({ profile, senderInfo, isLoading, error, refresh }),
    [profile, senderInfo, isLoading, error],
  );

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}
